import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Get user profile
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in getUserProfile:', error?.message);
        throw error;
      }
      console.log('Error fetching profile:', error?.message);
      return null;
    }

    // Convert snake_case to camelCase
    return {
      id: data?.id,
      email: data?.email,
      fullName: data?.full_name,
      avatarUrl: data?.avatar_url,
      phone: data?.phone,
      dateOfBirth: data?.date_of_birth,
      gender: data?.gender,
      height: data?.height,
      weight: data?.weight,
      activityLevel: data?.activity_level,
      dailyStepsGoal: data?.daily_steps_goal,
      dailyCaloriesGoal: data?.daily_calories_goal,
      dailyWaterGoal: data?.daily_water_goal,
      sleepGoal: data?.sleep_goal,
      dietaryPreferences: data?.dietary_preferences || [],
      healthConditions: data?.health_conditions || [],
      macroTargets: data?.macro_targets || { protein: 25, carbs: 50, fat: 25 },
      themeColorScheme: data?.theme_color_scheme,
      themeAccentColor: data?.theme_accent_color,
      themeFontSize: data?.theme_font_size,
      themeAnimations: data?.theme_animations,
      themeCompactMode: data?.theme_compact_mode,
      profileVisibility: data?.profile_visibility,
      dataSharing: data?.data_sharing,
      analyticsOptIn: data?.analytics_opt_in,
      marketingEmails: data?.marketing_emails,
      thirdPartySharing: data?.third_party_sharing,
      twoFactorEnabled: data?.two_factor_enabled,
      passwordChangedAt: data?.password_changed_at,
      emailDigestEnabled: data?.email_digest_enabled,
      weeklyDigestDay: data?.weekly_digest_day,
      mealRemindersEnabled: data?.meal_reminders_enabled,
      breakfastReminderTime: data?.breakfast_reminder_time,
      lunchReminderTime: data?.lunch_reminder_time,
      dinnerReminderTime: data?.dinner_reminder_time,
      achievementEmailsEnabled: data?.achievement_emails_enabled,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    throw error;
  }
};

// Update personal information
export const updatePersonalInfo = async (userId, personalInfo) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({
        full_name: personalInfo?.fullName,
        phone: personalInfo?.phone,
        date_of_birth: personalInfo?.dateOfBirth,
        gender: personalInfo?.gender,
        height: personalInfo?.height,
        weight: personalInfo?.weight
      })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updatePersonalInfo:', error?.message);
        throw error;
      }
      console.log('Error updating personal info:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in updatePersonalInfo:', error);
    throw error;
  }
};

// Update profile photo
export const updateProfilePhoto = async (userId, file) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${userId}-${Date.now()}.${fileExt}`;
    const filePath = `avatars/${fileName}`;

    // Upload file to storage
    const { error: uploadError } = await supabase?.storage?.from('avatars')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      console.log('Error uploading photo:', uploadError?.message);
      return { success: false, error: uploadError?.message };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase?.storage?.from('avatars')?.getPublicUrl(filePath);

    // Update user profile with new avatar URL
    const { data, error } = await supabase?.from('user_profiles')?.update({ avatar_url: publicUrl })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updateProfilePhoto:', error?.message);
        throw error;
      }
      console.log('Error updating avatar URL:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, avatarUrl: publicUrl };
  } catch (error) {
    console.error('Error in updateProfilePhoto:', error);
    throw error;
  }
};

// Update health preferences
export const updateHealthPreferences = async (userId, preferences) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({
        daily_steps_goal: preferences?.dailyStepsGoal,
        daily_calories_goal: preferences?.dailyCaloriesGoal,
        daily_water_goal: preferences?.dailyWaterGoal,
        sleep_goal: preferences?.sleepGoal,
        activity_level: preferences?.activityLevel,
        dietary_preferences: preferences?.dietaryPreferences,
        health_conditions: preferences?.healthConditions,
        macro_targets: preferences?.macroTargets
      })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updateHealthPreferences:', error?.message);
        throw error;
      }
      console.log('Error updating health preferences:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in updateHealthPreferences:', error);
    throw error;
  }
};

// Update theme customization
export const updateThemeSettings = async (userId, themeSettings) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({
        theme_color_scheme: themeSettings?.colorScheme,
        theme_accent_color: themeSettings?.accentColor,
        theme_font_size: themeSettings?.fontSize,
        theme_animations: themeSettings?.animations,
        theme_compact_mode: themeSettings?.compactMode
      })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updateThemeSettings:', error?.message);
        throw error;
      }
      console.log('Error updating theme settings:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in updateThemeSettings:', error);
    throw error;
  }
};

// Update privacy settings
export const updatePrivacySettings = async (userId, privacySettings) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({
        profile_visibility: privacySettings?.profileVisibility,
        data_sharing: privacySettings?.dataSharing,
        analytics_opt_in: privacySettings?.analyticsOptIn,
        marketing_emails: privacySettings?.marketingEmails,
        third_party_sharing: privacySettings?.thirdPartySharing
      })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in updatePrivacySettings:', error?.message);
        throw error;
      }
      console.log('Error updating privacy settings:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in updatePrivacySettings:', error);
    throw error;
  }
};

// Update password
export const updatePassword = async (currentPassword, newPassword) => {
  try {
    const { error } = await supabase?.auth?.updateUser({
      password: newPassword
    });

    if (error) {
      console.log('Error updating password:', error?.message);
      return { success: false, error: error?.message };
    }

    // Update password_changed_at timestamp
    const { data: { user } } = await supabase?.auth?.getUser();
    if (user) {
      await supabase?.from('user_profiles')?.update({ password_changed_at: new Date()?.toISOString() })?.eq('id', user?.id);
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updatePassword:', error);
    return { success: false, error: error?.message };
  }
};

// Toggle two-factor authentication
export const toggleTwoFactor = async (userId, enabled) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.update({ two_factor_enabled: enabled })?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error in toggleTwoFactor:', error?.message);
        throw error;
      }
      console.log('Error toggling 2FA:', error?.message);
      return { success: false, error: error?.message };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error in toggleTwoFactor:', error);
    throw error;
  }
};

// Export user data
export const exportUserData = async (userId) => {
  try {
    // Get user profile
    const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (profileError) {
      if (isSchemaError(profileError)) {
        console.error('Schema error in exportUserData:', profileError?.message);
        throw profileError;
      }
      console.log('Error fetching profile for export:', profileError?.message);
      return { success: false, error: profileError?.message };
    }

    // Get workout logs
    const { data: workouts } = await supabase?.from('workout_logs')?.select('*')?.eq('user_id', userId);

    // Get nutrition logs
    const { data: nutrition } = await supabase?.from('nutrition_logs')?.select('*')?.eq('user_id', userId);

    const exportData = {
      profile,
      workouts: workouts || [],
      nutrition: nutrition || [],
      exportDate: new Date()?.toISOString()
    };

    return { success: true, data: exportData };
  } catch (error) {
    console.error('Error in exportUserData:', error);
    throw error;
  }
};